import { RubyGemMark } from '../../../components/RubyGemMark';
/**
 * CredentialRefsPanel — provider credentials as SSM REFERENCES.
 *
 * Spec §9.2 / A5: the three plaintext API-key editors that used to live on
 * SuperAdminAIStatus (Vertex, OpenAI, Remove.bg) are deliberately NOT carried
 * over. Keys live in SSM SecureString; rotation is a parameter write. This
 * panel shows the PATH and a masked hint and never a decrypted value.
 */
import React, { useState } from 'react';
import { Check, Key, Play, ShieldCheck } from 'lucide-react';
import { api } from '../../../services/apiService';
import type { AiProviderDto } from '../../../services/aiControlApi';
import type { AiTabProps } from '../AiControlCenter';

interface Props {
  providers: AiProviderDto[];
  showToast: AiTabProps['showToast'];
}


// Credit economics (unit price + minimum purchase) moved to TokenEconomicsPanel
// on 2026-10-17: the token value now derives the $0.01 credit price.
const CredentialRefsPanel: React.FC<Props> = ({ providers }) => {
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState<{ ok: boolean; message: string; imageUrl?: string } | null>(null);

  const runTest = async () => {
    setTesting(true);
    setTestResult(null);
    try {
      const result = await api.superAdmin.testAISettings();
      setTestResult({
        ok: !!result.success,
        message: result.success ? 'Pipeline responded successfully.' : (result.message || 'Test failed'),
        imageUrl: result.testImageUrl,
      });
    } catch (err: any) {
      setTestResult({ ok: false, message: err?.message || 'Test failed' });
    } finally {
      setTesting(false);
    }
  };

  return (
    <>
      <div className="bg-white border border-stone-200 rounded-xl shadow-sm overflow-hidden">
        <div className="px-4 py-3 border-b border-stone-100">
          <h2 className="text-sm font-semibold text-gray-900 flex items-center gap-2">
            <ShieldCheck size={14} className="text-emerald-600" /> Credentials
          </h2>
          <p className="text-[11px] text-gray-500 mt-0.5">
            Every provider key is an SSM SecureString parameter. Rotate by writing the
            parameter — the router picks it up within its cache TTL with no redeploy.
            This page never displays a decrypted value.
          </p>
        </div>
        {providers.length === 0 ? (
          <div className="p-8 text-center text-sm text-gray-500">No providers registered.</div>
        ) : (
          <ul className="divide-y divide-stone-100">
            {providers.map((p) => (
              <li key={p.providerId} className="px-4 py-3">
                <div className="flex items-center justify-between gap-3">
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-gray-900">{p.label}</p>
                    <p className="text-[11px] text-gray-500 font-mono truncate flex items-center gap-1">
                      <Key size={10} className="flex-shrink-0" /> {p.credentialsRef}
                    </p>
                  </div>
                  <span className="text-[11px] font-mono text-gray-400 flex-shrink-0">
                    {p.credentialHint || '••••'}
                  </span>
                </div>
              </li>
            ))}
          </ul>
        )}
        <div className="px-4 py-3 border-t border-stone-100 bg-stone-50">
          <p className="text-[11px] text-gray-600 font-mono">
            aws ssm put-parameter --region us-west-1 --overwrite --type SecureString \
            --name /agenticpos/dev/ai/&lt;providerId&gt;/apiKey --value &lt;new key&gt;
          </p>
        </div>
      </div>

      <div className="bg-white border border-stone-200 rounded-xl shadow-sm overflow-hidden">
        <div className="px-4 py-3 border-b border-stone-100">
          <h2 className="text-sm font-semibold text-gray-900">Live pipeline test</h2>
          <p className="text-[11px] text-gray-500 mt-0.5">
            Generates one test image end to end. This is a real, billed call.
          </p>
        </div>
        <div className="p-4 space-y-3">
          <button
            type="button" onClick={() => void runTest()} disabled={testing}
            className="inline-flex items-center gap-2 px-4 py-2 min-h-[44px] text-sm font-medium bg-rose-600 text-white rounded-lg hover:bg-rose-700 disabled:opacity-50"
          >
            {testing ? <RubyGemMark size={16} drawing color="currentColor" /> : <Play size={16} />}
            Run test
          </button>
          {testResult && (
            <div className={`rounded-lg px-3 py-2 text-xs ${
              testResult.ok ? 'bg-emerald-50 text-emerald-800' : 'bg-rose-50 text-rose-700'
            }`}>
              <p className="flex items-center gap-1.5 font-medium">
                {testResult.ok && <Check size={12} />} {testResult.message}
              </p>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default CredentialRefsPanel;
